<template>
  <div class="variable-force-work-viz">
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$W = ∫ F(x) dx ≈ Σ Fi · Δxi$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 物理仿真演示区 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="160" viewBox="0 0 600 160">
          <!-- 轨道 -->
          <line x1="50" y1="120" x2="550" y2="120" stroke="#334155" stroke-width="2" />
          
          <!-- 物体 (m) -->
          <g :transform="`translate(${50 + currentX * 10}, 80)`">
             <rect x="0" y="0" width="40" height="40" fill="#0ea5e9" rx="4" />
             <text x="20" y="25" text-anchor="middle" fill="#fff" font-size="12" font-weight="900">m</text>
             
             <!-- 力矢量 F (动态长度) -->
             <line x1="40" y1="20" :x2="40 + currentForce * 2" y2="20" stroke="#ef4444" stroke-width="4" marker-end="url(#arr-red)" />
             <text :x="45 + currentForce * 2" y="25" fill="#ef4444" font-size="12" font-weight="bold">F(x)</text>
          </g>
          
          <!-- 弹簧 (若在变力模式) -->
          <path v-if="mode === 'spring'" :d="springPath" fill="none" stroke="#64748b" stroke-width="2" opacity="0.5" />
          
          <defs>
            <marker id="arr-red" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
              <path d="M0,0 L6,2 L0,4 Z" fill="#ef4444" />
            </marker>
          </defs>
        </svg>
        
        <div class="hud-top">
           <div class="stat-glass">
              <label>当前位置 x</label>
              <span>{{ currentX.toFixed(1) }} m</span>
           </div>
           <div class="stat-glass red">
              <label>实时受力 F</label>
              <span>{{ currentForce.toFixed(1) }} N</span>
           </div>
        </div>
      </div>

      <!-- F-x 图像区 (微元法核心) -->
      <div class="graph-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 600 220">
          <!-- 坐标系 -->
          <line x1="60" y1="180" x2="560" y2="180" stroke="#475569" stroke-width="1" />
          <line x1="60" y1="20" x2="60" y2="190" stroke="#475569" stroke-width="1" />
          <text x="560" y="195" fill="#64748b" font-size="10">位移 x</text>
          <text x="35" y="25" fill="#64748b" font-size="10">力 F</text>

          <!-- 微元矩形 (Riemann Sum) -->
          <g v-if="showMicro">
            <rect v-for="(r, i) in rects" :key="i" 
                  :x="r.x" :y="r.y" :width="r.w" :height="r.h" 
                  fill="rgba(14, 165, 233, 0.3)" stroke="rgba(14, 165, 233, 0.5)" />
          </g>

          <!-- 真实函数曲线 -->
          <path :d="curvePath" fill="none" stroke="#fff" stroke-width="2" stroke-dasharray="0" />
          
          <!-- 当前扫描线 -->
          <line :x1="60 + currentX * 10" y1="20" :x2="60 + currentX * 10" y2="180" stroke="#facc15" stroke-width="1" stroke-dasharray="4,2" />

          <!-- 等效平均力参考线 (仅在特定模式显示) -->
          <g v-if="showEquivalent && currentX > 10">
             <line x1="60" :y1="180 - avgForce * 2" :x2="60 + currentX * 10" :y2="180 - avgForce * 2" stroke="#10b981" stroke-width="1.5" stroke-dasharray="5,3" />
             <text :x="70" :y="175 - avgForce * 2" fill="#10b981" font-size="9" font-weight="bold">等效恒力 F_avg = W/s</text>
          </g>
        </svg>
        
        <div class="accumulated-work glass">
           <label>累计做功 (图象面积)</label>
           <div class="val">{{ totalWork.toFixed(1) }} J</div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
         <div class="card glass">
            <div class="label-row">选择变力类型</div>
            <div class="btn-group">
               <el-button size="mini" :type="mode === 'const' ? 'primary' : 'info'" @click="mode = 'const'">恒力 (F=C)</el-button>
               <el-button size="mini" :type="mode === 'spring' ? 'primary' : 'info'" @click="mode = 'spring'">线性变力 (F=kx)</el-button>
               <el-button size="mini" :type="mode === 'sine' ? 'primary' : 'info'" @click="mode = 'sine'">非线性功 (震荡)</el-button>
            </div>
         </div>
         
         <div class="card glass">
            <div class="flex-center gap-10">
               <el-checkbox v-model="showMicro">启用微元法解析 (Δx -> dx)</el-checkbox>
               <div v-if="showMicro" class="flex-1">
                  <span class="tiny-label">微元划分密度: {{ rectCount }}</span>
                  <el-slider v-model="rectCount" :min="5" :max="100" size="mini"></el-slider>
               </div>
            </div>
         </div>

         <div class="action-row">
            <el-button type="success" size="small" @click="start">开始推行并计算做功</el-button>
            <el-button size="small" @click="reset">重置</el-button>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VariableForceWorkViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'spring',
      currentX: 0,
      totalWork: 0,
      showMicro: true,
      rectCount: 20,
      isRunning: false,
      timer: null
    }
  },
  computed: {
    displayTitle() {
      return this.knowledgePoint || '变力做功：微元法与积分思想';
    },
    currentForce() {
      const x = this.currentX;
      if (this.mode === 'const') return 30;
      if (this.mode === 'spring') return x * 5;
      if (this.mode === 'sine') return 20 + Math.sin(x) * 15;
      return 0;
    },
    avgForce() {
      return this.currentX > 0 ? this.totalWork / this.currentX : 0;
    },
    curvePath() {
      let d = "";
      for (let x = 0; x <= 50; x += 1) {
         const f = this.getForceAt(x);
         const px = 60 + x * 10;
         const py = 180 - f * 2;
         d += (x === 0 ? "M" : "L") + ` ${px} ${py}`;
      }
      return d;
    },
    rects() {
      const arr = [];
      const stepX = 50 / this.rectCount;
      for (let i = 0; i < this.rectCount; i++) {
         const x = i * stepX;
         if (x > this.currentX) break;
         const f = this.getForceAt(x);
         arr.push({
            x: 60 + x * 10,
            y: 180 - f * 2,
            w: stepX * 10,
            h: f * 2
         });
      }
      return arr;
    },
    springPath() {
      const startX = 50;
      const endX = 50 + this.currentX * 10;
      const segs = 15;
      const step = (endX - startX) / segs;
      let d = `M ${startX} 100`;
      for (let i = 1; i <= segs; i++) {
         const x = startX + i * step;
         const y = 100 + (i % 2 === 0 ? -10 : 10) * (i === segs ? 0 : 1);
         d += ` L ${x} ${y}`;
      }
      return d;
    }
  },
  watch: {
    mode() { this.reset(); },
    knowledgePoint() { this.detectMode(); }
  },
  mounted() {
    this.detectMode();
  },
  methods: {
    detectMode() {
       const kp = (this.knowledgePoint || '').toString();
       this.reset();
       if (kp.includes('积分') || kp.includes('微元')) {
          this.mode = 'sine';
          this.showMicro = true;
          this.rectCount = 40;
       } else if (kp.includes('转化') || kp.includes('恒力')) {
          this.mode = 'spring';
          this.showMicro = false;
          this.showEquivalent = true;
       } else {
          this.mode = 'spring';
          this.showMicro = true;
          this.rectCount = 20;
          this.showEquivalent = false;
       }
    },
    getForceAt(x) {
      if (this.mode === 'const') return 30;
      if (this.mode === 'spring') return x * 1.2;
      if (this.mode === 'sine') return 20 + Math.sin(x * 0.4) * 15;
      return 0;
    },
    start() {
      if (this.isRunning) return;
      this.reset();
      this.isRunning = true;
      this.animate();
    },
    reset() {
      this.isRunning = false;
      this.currentX = 0;
      this.totalWork = 0;
      cancelAnimationFrame(this.timer);
    },
    animate() {
      if (!this.isRunning) return;
      const dx = 0.2;
      const f = this.getForceAt(this.currentX);
      
      this.totalWork += f * dx;
      this.currentX += dx;

      if (this.currentX >= 50) {
        this.isRunning = false;
        this.currentX = 50;
      } else {
        this.timer = requestAnimationFrame(this.animate);
      }
    }
  }
}
</script>

<style scoped lang="scss">
.variable-force-work-viz { flex: 1; display: flex; flex-direction: column; gap: 10px; height: 100%; color: #fff; }
.viz-header { display: flex; justify-content: space-between; align-items: center; .formula-chip { background: #0ea5e9; color: #fff; padding: 4px 12px; border-radius: 6px; font-weight: bold; font-family: 'Times New Roman', serif; } }
.main-layout { display: flex; flex-direction: column; gap: 12px; }
.dark-stage { background: #0f172a; border-radius: 12px; border: 1px solid #334155; position: relative; overflow: hidden; }
.simulation-stage { min-height: 160px; }
.graph-stage { min-height: 220px; }
.hud-top { position: absolute; top: 15px; left: 15px; display: flex; gap: 10px; }
.stat-glass { background: rgba(30, 41, 59, 0.7); border-left: 3px solid #0ea5e9; padding: 4px 12px; border-radius: 6px; label { display: block; font-size: 10px; color: #94a3b8; } span { font-size: 14px; font-weight: bold; font-family: monospace; } &.red { border-left-color: #ef4444; } }
.accumulated-work { position: absolute; top: 15px; right: 15px; background: rgba(14, 165, 233, 0.1); border: 1px solid #0ea5e9; padding: 8px 15px; border-radius: 8px; text-align: center; label { display: block; font-size: 10px; color: #0ea5e9; } .val { font-size: 1.2rem; font-weight: bold; color: #facc15; } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.card { background: rgba(255,255,255,0.05); padding: 12px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.1); .label-row { font-size: 11px; color: #94a3b8; margin-bottom: 8px; } }
.btn-group { display: flex; gap: 6px; }
.flex-center { display: flex; align-items: center; }
.tiny-label { font-size: 10px; color: #64748b; display: block; }
.gap-10 { gap: 10px; }
.action-row { display: flex; gap: 10px; justify-content: center; }
</style>
